import { getAssetUrl } from "@/constant/assets";

class SystemSound {
  private static ctx: AudioContext | null = null;
  private static buffers: Record<string, AudioBuffer> = {};
  private static initialized = false;
  private static currentSources: AudioBufferSourceNode[] = []; // 跟踪当前播放的音源
  private static loadingPromises: Map<string, Promise<AudioBuffer>> = new Map(); // 跟踪正在加载的音效

  /**
   * 音效配置：分为高优先级和低优先级
   * - priority: 立即加载（常用音效）
   * - lazy: 按需加载（不常用音效）
   */
  private static soundConfig = {
    priority: {
      correct: getAssetUrl('assets/audio/correct.mp3'),
      wrong: getAssetUrl('assets/audio/wrong.mp3'),
      coin: getAssetUrl('assets/audio/coin.wav'),
    },
    lazy: {
      // 示例：可以添加更多低优先级音效
      spin: getAssetUrl('assets/audio/spin_audio.wav'),
      scroll: getAssetUrl('assets/audio/sound_of_wheel_turing.wav'),
      claim: getAssetUrl('assets/audio/chest-3.wav'),
    }
  };

  /** 初始化 AudioContext 和高优先级音效 */
  static async init() {
    if (this.initialized) return;

    // 创建 AudioContext
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    this.ctx = ctx;
    this.initialized = true;

    // 并行加载高优先级音效
    await this.loadSounds(this.soundConfig.priority);

    // 后台加载低优先级音效（不阻塞）
    if (Object.keys(this.soundConfig.lazy).length > 0) {
      this.loadSounds(this.soundConfig.lazy).catch(error => {
        console.warn('[SystemSound] Failed to load lazy sounds:', error);
      });
    }
  }

  /**
   * 并行加载多个音效
   * 使用 Promise.all 实现并行下载和解码
   */
  private static async loadSounds(sounds: Record<string, string>) {
    const loadPromises = Object.entries(sounds).map(async ([key, path]) => {
      try {
        const res = await fetch(path);
        const buf = await res.arrayBuffer();
        const audioBuffer = await this.ctx!.decodeAudioData(buf);
        this.buffers[key] = audioBuffer;
        console.log(`[SystemSound] Loaded: ${key}`);
        return { key, success: true };
      } catch (error) {
        console.error(`[SystemSound] Failed to load ${key}:`, error);
        return { key, success: false };
      }
    });

    await Promise.all(loadPromises);
  }

  /**
   * 按需加载单个音效（用于懒加载）
   * 如果音效未加载，自动下载并缓存
   */
  private static async ensureLoaded(name: string): Promise<AudioBuffer | null> {
    // 已经加载
    if (this.buffers[name]) {
      return this.buffers[name];
    }

    // 正在加载中，返回加载 Promise
    if (this.loadingPromises.has(name)) {
      return this.loadingPromises.get(name)!;
    }

    // 查找音效路径
    const allSounds = { ...this.soundConfig.priority, ...this.soundConfig.lazy };
    const path = allSounds[name as keyof typeof allSounds];

    if (!path) {
      console.warn(`[SystemSound] Sound not found: ${name}`);
      return null;
    }

    // 开始加载
    const loadPromise = (async () => {
      try {
        console.log(`[SystemSound] Loading on-demand: ${name}`);
        const res = await fetch(path);
        const buf = await res.arrayBuffer();
        const audioBuffer = await this.ctx!.decodeAudioData(buf);
        this.buffers[name] = audioBuffer;
        return audioBuffer;
      } catch (error) {
        console.error(`[SystemSound] Failed to load ${name}:`, error);
        throw error;
      } finally {
        this.loadingPromises.delete(name);
      }
    })();

    this.loadingPromises.set(name, loadPromise);
    return loadPromise;
  }

  /** 播放音效（支持懒加载） */
  static async play(name: string, volume = 0.3) {
    if (!this.ctx || !this.initialized) return;

    // 确保 AudioContext 已恢复（修复小米浏览器等严格浏览器的问题）
    if (this.ctx.state === 'suspended') {
      await this.ctx.resume(); // 添加 await，等待恢复完成
    }

    // 确保音效已加载（支持懒加载）
    const buffer = await this.ensureLoaded(name);
    if (!buffer) {
      console.warn(`[SystemSound] Failed to load buffer: ${name}`);
      return;
    }

    const source = this.ctx.createBufferSource();
    const gainNode = this.ctx.createGain();
    gainNode.gain.value = volume;

    source.buffer = buffer;
    source.connect(gainNode);
    gainNode.connect(this.ctx.destination);
    source.start(0);

    // 跟踪音源，并在播放结束后自动清理
    this.currentSources.push(source);
    source.onended = () => {
      const index = this.currentSources.indexOf(source);
      if (index > -1) {
        this.currentSources.splice(index, 1);
      }
    };
  }

  /** 停止所有正在播放的音效 */
  static stop() {
    if (!this.ctx || !this.initialized) return;

    // 停止所有正在播放的音源
    this.currentSources.forEach(source => {
      try {
        source.stop();
      } catch (e) {
        // 音源可能已经停止，忽略错误
      }
    });

    // 清空音源数组
    this.currentSources = [];
  }

  /** 停止特定名称的音效 */
  static stopSpecific(name: string) {
    if (!this.ctx || !this.initialized) return;

    const buffer = this.buffers[name];
    if (!buffer) return;

    // 找到并停止所有匹配的音源
    this.currentSources = this.currentSources.filter(source => {
      if (source.buffer === buffer) {
        try {
          source.stop();
        } catch (e) {
          // 音源可能已经停止，忽略错误
        }
        return false; // 从数组中移除
      }
      return true; // 保留其他音源
    });
  }

  static isPlaying(name: string) {
    const buffer = this.buffers[name];
    if (!buffer) return false;
    return this.currentSources.some(source => source.buffer === buffer);
  }
}

// ✅ 导出保持原有函数接口（业务代码无需改动）
async function playCorrectSound(volume = 0.3) {
  try {
    await SystemSound.init();
    await SystemSound.play('correct', volume);
  } catch (error) {
    console.error('Error playing correct sound:', error);
  }
}

async function playWrongSound(volume = 0.3) {
  try {
    await SystemSound.init();
    await SystemSound.play('wrong', volume);
  } catch (error) {
    console.error('Error playing wrong sound:', error);
  }
}

async function playSpinSound() {
  try {
    await SystemSound.init();
    if (SystemSound.isPlaying('spin')) return;
    await SystemSound.play('spin', 0.5);
  } catch (error) {
    console.error('Error playing spin sound:', error);
  }
}

async function playScrollSound() {
  try {
    await SystemSound.init();
    // 先停止之前的 scroll 音效（如果有的话）
    SystemSound.stopSpecific('scroll');
    // 播放新的 scroll 音效
    await SystemSound.play('scroll', 0.2);
  } catch (error) {
    console.error('Error playing scroll sound:', error);
  }
}

async function stopSound() {
  try {
    SystemSound.stop();
  } catch (error) {
    console.error('Error stopping sound:', error);
  }
}

async function playCoinSound() {
  try {
    await SystemSound.init();
    await SystemSound.play('coin', 0.3);
  } catch (error) {
    console.error('Error playing coin sound:', error);
  }
}

async function stopSpecificSound(name: string) {
  try {
    SystemSound.stopSpecific(name);
  } catch (error) {
    console.error(`Error stopping ${name} sound:`, error);
  }
}

async function playClaimSound() {
  try {
    await SystemSound.init();
    await SystemSound.play('claim', 0.3);
  } catch (error) {
    console.error('Error playing claim sound:', error);
  }
}

export {
  playCorrectSound,
  playWrongSound,
  playSpinSound,
  playScrollSound,
  playCoinSound,
  playClaimSound,
  stopSound,
  stopSpecificSound,

  SystemSound,
};
